import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import FormField from './FormField';
import FileUpload from './FileUpload';
import ReactSelect from '../../Shared/ReactSelect/ReactSelect';
import Config from '../../../Config';

const LeadContactFormContent = ({
  iContent,
  formData,
  errors,
  isSubmitting,
  uploadedFiles,
  fileUploadError,
  onFieldChange,
  onFileChange,
  onFileRemove,
  onSubmit,
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Helper function to strip HTML tags
  const stripHtml = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Extract content properties
  const backgroundColor = iContent.backgroundColor?.value || '#ffffff';
  const headline = iContent.headline?.value;
  const secondHeadline = iContent.secondHeadline?.value;
  const paragraph = iContent.paragraph?.value;
  const buttonText = iContent.buttonText?.value || 'שלח';
  const subjectListLabel = iContent.subjectListLabel?.value || 'נושא הפניה';
  const showAgreement = iContent.showAgreement?.value || false;

  // GlobalConfig lands in Redux as state.initApp after getGlobalConfig; Config is updated in parallel but
  // is not reactive — useSelector subscribes so we re-render when the async payload arrives.
  const initApp = useSelector((state) => state.initApp);
  const blockAgreements = iContent.potentialCustomerAgreements;
  const gc = initApp && typeof initApp === 'object' && !Array.isArray(initApp) ? initApp : {};
  const potentialCustomerAgreements = {
    defultText:
      gc.potentialCustomerDefaulText ||
      Config.potentialCustomerDefaulText ||
      blockAgreements?.defaulText?.value ||
      '',
    bottomText:
      gc.potentialCustomerBottomText ||
      Config.potentialCustomerBottomText ||
      blockAgreements?.bottomText?.value ||
      '',
    openToolTipButton:
      gc.potentialCustomerTextButton ||
      Config.potentialCustomerTextButton ||
      blockAgreements?.openTooltipButton?.value ||
      '',
    tooltipText:
      gc.potentialCustomerTooltipText ||
      Config.potentialCustomerTooltipText ||
      blockAgreements?.tooltipText?.value ||
      '',
  };

  const termsTextRich =
    gc.loginPageLegalText || Config.loginPageLegalText || '';

const subjectList = iContent.subjectList?.expandedValue?.map((x) => ({
  subjectName: x.subjectName?.value,
  subjectCode: x.subjectCode?.value,
})) || [];
  // Block styling
  const blockStyles = {
    backgroundColor: backgroundColor,
    opacity: iContent.blockOpacity?.value || 1,
    margin: iContent.margin?.value || undefined,
    padding: iContent.padding?.value || undefined,
  };

  // Field configurations
  const fields = [
    {
      name: 'firstName',
      type: 'text',
      show: iContent.firstNameShow?.value,
      required: iContent.firstNameRequired?.value,
      placeholder: iContent.firstNamePlaceholder?.value || 'שם פרטי',
    },
    {
      name: 'lastName',
      type: 'text',
      show: iContent.lastNameShow?.value,
      required: iContent.lastNameRequired?.value,
      placeholder: iContent.lastNamePlaceholder?.value || 'שם משפחה',
    },
    {
      name: 'identity',
      type: 'text',
      show: iContent.idShow?.value,
      required: iContent.idRequired?.value,
      placeholder: iContent.idPlaceholder?.value || 'תעודת זהות',
      maxLength: 9,
    },
    {
      name: 'payment',
      type: 'text',
      show: iContent.paymentDigitsShow?.value,
      required: iContent.paymentDigitsRequired?.value,
      placeholder: iContent.paymentDigitsPlaceholder?.value || '4 ספרות אחרונות של אמצעי תשלום',
      maxLength: 4,
    },
    {
      name: 'phoneNumber',
      type: 'tel',
      show: iContent.cellPhoneShow?.value,
      required: iContent.cellPhoneRequired?.value,
      placeholder: iContent.cellPhonePlaceholder?.value || 'טלפון נייד',
      maxLength: 10,
    },
    {
      name: 'additionalPhone',
      type: 'tel',
      show: iContent.otherPhoneShow?.value,
      required: iContent.otherPhoneRequired?.value,
      placeholder: iContent.otherPhonePlaceholder?.value || 'טלפון אחר',
      maxLength: 10,
    },
    {
      name: 'emailAddress',
      type: 'email',
      show: iContent.emailShow?.value,
      required: iContent.emailRequired?.value,
      placeholder: iContent.emailPlaceholder?.value || 'דואר אלקטרוני',
    },
    {
      name: 'companyName',
      type: 'text',
      show: iContent.companyNameShow?.value,
      required: iContent.companyNameRequired?.value,
      placeholder: iContent.companyNamePlaceholder?.value || 'שם חברה',
    },
    {
      name: 'generalField',
      type: 'text',
      show: iContent.generalShow?.value,
      required: iContent.generalRequired?.value,
      placeholder: iContent.generalPlaceholder?.value || 'כללי',
    },
    {
      name: 'subjectIndex',
      type: 'select',
      show: iContent.isShowSubject?.value && subjectList.length > 0,
      required: iContent.isSubjectRequired?.value,
      placeholder: subjectListLabel,
      options: subjectList.map((subject, index) => ({
        value: (index + 1).toString(), // 1-based index like ExtendedLeadBlock
        label: subject.subjectName,
      })),
    },
    {
      name: 'content',
      type: 'textarea',
      show: iContent.contactMessageShow?.value,
      required: iContent.contactMessageRequired?.value,
      placeholder: iContent.contactMessagePlaceholder?.value || 'הודעה',
    },
  ];

  const visibleFields = fields.filter((field) => field.show);

  // Separate subject field from other fields
  const subjectField = visibleFields.find(f => f.name === 'subjectIndex');
  const otherFields = visibleFields.filter(f => f.name !== 'subjectIndex');

  // File upload configuration
  const allowFileUpload = iContent.allowFileUpload?.value || false;
  const maxFileCount = iContent.maxFileCount?.value || 3;
  const allowedFileTypes = iContent.allowedFileTypes?.value || '';
  const fileUploadExplanation = iContent.fileUploadExplanation?.value || '';
  const fileCountExceededMessage = iContent.fileCountExceededMessage?.value || 'חרגת ממספר הקבצים המקסימלי';

  return (
    <div className="lead-contact-form-block" style={blockStyles}>
      <div className="form-div">
        {/* Title Section */}
        <div className="title-div">
          {headline && <div className="headline" dangerouslySetInnerHTML={{__html: headline}} />}
          {secondHeadline && <div className="second-headline" dangerouslySetInnerHTML={{__html: secondHeadline}} />}
        </div>

        {/* Paragraph */}
        {paragraph && <div className="paragraph" dangerouslySetInnerHTML={{__html: paragraph}} />}

        {/* Fields and Button Section */}
        <div className="fields-and-btn-div">
          {/* Fields Container */}
          <div className="fields-div">
            {visibleFields.length > 0 ? (
              <form onSubmit={onSubmit} noValidate>
                {/* Group fields into rows (2 per row on desktop) */}
                {(() => {
                  const rows = [];
                  // Collect all regular fields (not textarea), including subject dropdown
                  let regularFields = otherFields.filter(f => f.type !== 'textarea');
                  const textareaField = otherFields.find(f => f.type === 'textarea');

                  // Determine if we should position dropdown last
                  let allFieldsIncludingSubject = [...regularFields];
                  if (subjectField) {
                    allFieldsIncludingSubject.push(subjectField);
                  }

                  const hasOddNumberOfFields = allFieldsIncludingSubject.length % 2 !== 0;

                  // If odd number and subject exists, position subject last
                  if (hasOddNumberOfFields && subjectField) {
                    // Pair all regular fields first (excluding subject)
                    for (let i = 0; i < regularFields.length; i += 2) {
                      const fieldsInRow = regularFields.slice(i, i + 2);
                      rows.push(
                        <div key={`row-${i}`} className="row-div">
                          {fieldsInRow.map((field) => (
                            <FormField
                              key={field.name}
                              name={field.name}
                              type={field.type}
                              placeholder={field.placeholder}
                              value={formData[field.name]}
                              error={errors[field.name]}
                              required={field.required}
                              maxLength={field.maxLength}
                              options={field.options}
                              onChange={(value) => onFieldChange(field.name, value)}
                              disabled={isSubmitting}
                            />
                          ))}
                        </div>
                      );
                    }
                    // Add subject dropdown as full-width last row
                    rows.push(
                      <div key="subject-row" className="row-div full-width">
                        <ReactSelect
                          name={subjectField.name}
                          placeholder={subjectField.placeholder}
                          options={subjectField.options}
                          value={subjectField.options.find(opt => opt.value === formData[subjectField.name]) || null}
                          onChange={(selectedOption) => onFieldChange(subjectField.name, selectedOption?.value || '')}
                          isDisabled={isSubmitting}
                          required={subjectField.required}
                          error={errors[subjectField.name]}
                          placeholderColor="#747374"
                        />
                      </div>
                    );
                  } else {
                    // Even number or no subject: pair all fields normally including subject
                    if (subjectField) {
                      regularFields = [...regularFields, subjectField];
                    }

                    for (let i = 0; i < regularFields.length; i += 2) {
                      const fieldsInRow = regularFields.slice(i, i + 2);
                      rows.push(
                        <div key={`row-${i}`} className="row-div">
                          {fieldsInRow.map((field) => {
                            // Render ReactSelect for subject field
                            if (field.name === 'subjectIndex') {
                              return (
                                <ReactSelect
                                  key={field.name}
                                  name={field.name}
                                  placeholder={field.placeholder}
                                  options={field.options}
                                  value={field.options.find(opt => opt.value === formData[field.name]) || null}
                                  onChange={(selectedOption) => onFieldChange(field.name, selectedOption?.value || '')}
                                  isDisabled={isSubmitting}
                                  required={field.required}
                                  error={errors[field.name]}
                                  placeholderColor="#747374"
                                />
                              );
                            }
                            // Render regular FormField for other fields
                            return (
                              <FormField
                                key={field.name}
                                name={field.name}
                                type={field.type}
                                placeholder={field.placeholder}
                                value={formData[field.name]}
                                error={errors[field.name]}
                                required={field.required}
                                maxLength={field.maxLength}
                                options={field.options}
                                onChange={(value) => onFieldChange(field.name, value)}
                                disabled={isSubmitting}
                              />
                            );
                          })}
                        </div>
                      );
                    }
                  }

                  // Add textarea field (full width)
                  if (textareaField) {
                    rows.push(
                      <div key="textarea-row" className="row-div full-width">
                        <FormField
                          key={textareaField.name}
                          name={textareaField.name}
                          type={textareaField.type}
                          placeholder={textareaField.placeholder}
                          value={formData[textareaField.name]}
                          error={errors[textareaField.name]}
                          required={textareaField.required}
                          maxLength={textareaField.maxLength}
                          options={textareaField.options}
                          onChange={(value) => onFieldChange(textareaField.name, value)}
                          disabled={isSubmitting}
                        />
                      </div>
                    );
                  }

                  return rows;
                })()}

                {/* File Upload Section */}
                {allowFileUpload && (
                  <FileUpload
                    allowedFileTypes={allowedFileTypes}
                    maxFileCount={maxFileCount}
                    uploadedFiles={uploadedFiles}
                    explanationText={fileUploadExplanation}
                    errorMessage={fileUploadError}
                    onFileChange={onFileChange}
                    onFileRemove={onFileRemove}
                    disabled={isSubmitting}
                  />
                )}

                {/* Hidden submit for form submission */}
                <button type="submit" style={{display: 'none'}} />
              </form>
            ) : (
              <div className="no-fields-message">
                <p>לא הוגדרו שדות להצגה בטופס זה.</p>
              </div>
            )}
          </div>

          {/* Potential Customer Agreements or Terms Text */}
          {showAgreement && potentialCustomerAgreements && potentialCustomerAgreements.defultText ? (
            <div className="potential-customer-agreements">
              <div className="agreements-first-row">
                <span className="defultText">{stripHtml(potentialCustomerAgreements.defultText)}</span>{' '}
                <span
                  className="extended-text-button"
                  onClick={() => {
                    setIsTooltipOpen(!isTooltipOpen);
                    setIsOverlayOpen(!isOverlayOpen);
                  }}
                >
                  {potentialCustomerAgreements.openToolTipButton}
                </span>
              </div>

              {isOverlayOpen && (
                <div className="lead-tooltip-inline">
                  <div className="lead-tooltip-content">
                    <span
                      className="close-tooltip"
                      onClick={() => {
                        setIsTooltipOpen(!isTooltipOpen);
                        setIsOverlayOpen(!isOverlayOpen);
                      }}
                    >
                      X
                    </span>
                    <span className="extended-text">{stripHtml(potentialCustomerAgreements.tooltipText)}</span>
                  </div>
                </div>
              )}

              <div className="agreements-bottom-row">
                <span className="bottomText"> {stripHtml(potentialCustomerAgreements.bottomText)}</span>
              </div>
            </div>
          ) : (
            termsTextRich && (
            <div className="terms-text-rich">
              <span className="terms-text" dangerouslySetInnerHTML={{__html: termsTextRich}}></span>
            </div>
            )
          )}

          {/* Send Button */}
          {visibleFields.length > 0 && (
            <button
              type="button"
              className="send-button"
              onClick={onSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'שולח...' : buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadContactFormContent;

